import { useState, type ReactElement } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { current_language } from '../i18n/index.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';
import { friendly_error } from '../lib/friendly-error.js';
import { format_inr } from '../lib/format.js';
import { NotifyMenu } from '../components/NotifyMenu.js';

interface Subscriber {
  id: string;
  name_ta: string;
  name_en: string;
  phone: string;
  status: string;
  risk_classification: string;
  town_ta?: string | null;
  town_en?: string | null;
  // Populated when the proc is called with with_totals=true.
  total_paid_paise?: number;
  outstanding_paise?: number;
  chit_count?: number;
}

type RiskFilter = 'all' | 'low' | 'medium' | 'high';

function initials(name: string | null | undefined): string {
  if (!name) return '··';
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '··';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]!.charAt(0) + p[p.length - 1]!.charAt(0)).toUpperCase();
}

export function SubscribersPage(): ReactElement {
  const lang = current_language();
  const nav = useNavigate();
  const [search, set_search] = useState('');
  const [risk, set_risk] = useState<RiskFilter>('all');
  // Cursor-paginated infinite query — 50 rows per page; "Show more" reveals
  // the next page using the `next_cursor` the backend returns. Pages
  // accumulate in memory so client-side search/filter operate on everything
  // the operator has loaded so far. with_totals enriches each row with
  // cross-chit total_paid_paise + outstanding_paise + chit_count via two
  // parallel aggregations per page (no per-row N+1).
  // 10 fits the typical above-the-fold viewport without scroll on a 13"
  // laptop, makes the "Show more" affordance visible for any tenant with
  // more than 10 members, and keeps each fetch's aggregation cheap. The
  // operator can also use the search box (client-side) on whatever is
  // loaded — pages accumulate, they don't replace.
  const PAGE_SIZE = 10;
  const q = useInfiniteQuery({
    queryKey: ['members', 'with_totals'],
    queryFn: ({ pageParam }) =>
      orpc.subscribers.list({
        with_totals: true,
        limit: PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    // `next_cursor` is `string | null` server-side; null means "no more pages".
    // React Query reads `undefined` (not null) as "stop", so coerce here.
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  // Flatten every fetched page into one in-memory list. React Query keeps
  // the pages cached so toggling filters / re-rendering doesn't refetch.
  const all = (q.data?.pages.flatMap((p) => p.subscribers ?? []) ?? []) as Subscriber[];
  const loaded_count = all.length;
  const has_more = q.hasNextPage ?? false;
  const active = all.filter((s) => s.status === 'active').length;
  const defaulted = all.filter((s) => s.status === 'defaulted').length;
  // Aggregate totals across the whole list (filtered + unfiltered both shown).
  const all_paid = all.reduce((s, m) => s + (m.total_paid_paise ?? 0), 0);
  const all_outstanding = all.reduce((s, m) => s + (m.outstanding_paise ?? 0), 0);

  const filtered = all.filter((s) => {
    if (risk !== 'all' && s.risk_classification !== risk) return false;
    if (!search.trim()) return true;
    const hay = `${s.name_en} ${s.name_ta} ${s.phone}`.toLowerCase();
    return hay.includes(search.trim().toLowerCase());
  });

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('உறுப்பினர்கள்', 'Members')}</div>
          <div className="h-sub">
            {all.length} {t('மொத்தம்', 'total')} · {active} {t('செயலில்', 'active')} · {defaulted}{' '}
            {t('பற்றாக்குறை', 'in default')} ·{' '}
            <span className="mono" style={{ color: 'var(--good)' }}>{format_inr(all_paid)}</span>{' '}
            {t('வசூலானது', 'collected')} ·{' '}
            <span className="mono" style={{ color: 'var(--warn)' }}>{format_inr(all_outstanding)}</span>{' '}
            {t('நிலுவை', 'outstanding')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {filtered.length > 0 && (
            <NotifyMenu
              bulk={{
                kind: 'members',
                ...(search.trim() ? { search: search.trim() } : {}),
              }}
              estimated_count={filtered.length}
              intent="custom"
              variant="inline"
            />
          )}
          <button className="btn primary" onClick={() => nav('/subscribers/new')}>
            <Icon.plus /> {t('புதிய உறுப்பினர்', 'New subscriber')}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="filters">
          <div className="searchbox" style={{ width: 260, margin: 0 }}>
            <Icon.search />
            <input
              value={search}
              onChange={(e) => set_search(e.target.value)}
              placeholder={t('பெயர், ஃபோன்...', 'Name, phone…')}
            />
          </div>
          <span style={{ width: 1, height: 18, background: 'var(--line)' }} />
          {(['all', 'low', 'medium', 'high'] as RiskFilter[]).map((r) => (
            <button key={r} className={'chip' + (risk === r ? ' on' : '')} onClick={() => set_risk(r)}>
              {r === 'all'
                ? t('அனைத்தும்', 'All')
                : r === 'low'
                  ? t('குறைந்த', 'Low risk')
                  : r === 'medium'
                    ? t('நடுத்தர', 'Medium')
                    : t('உயர்', 'High')}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
            {filtered.length} {t('பேர்', 'members')}
          </span>
        </div>

        {q.isLoading && <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: 14 }}>Loading…</div>}
        {q.error && <div style={{ color: 'var(--bad)', fontSize: 13, padding: 14 }}>{friendly_error(q.error)}</div>}
        {q.data && filtered.length === 0 && (
          <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: 24, textAlign: 'center' }}>
            {all.length === 0 ? t('இன்னும் உறுப்பினர்கள் இல்லை', 'No members yet') : t('பொருத்தம் இல்லை', 'No matches')}
          </div>
        )}
        {filtered.length > 0 && (
          <table className="sub-table">
            <thead>
              <tr>
                <th>{t('உறுப்பினர்', 'Member')}</th>
                <th style={{ textAlign: 'center' }}>{t('சீட்டுகள்', 'Chits')}</th>
                <th style={{ textAlign: 'right' }}>{t('வசூலானது', 'Paid')}</th>
                <th style={{ textAlign: 'right' }}>{t('நிலுவை', 'Outstanding')}</th>
                <th>{t('ரிஸ்க்', 'Risk')}</th>
                <th>{t('நிலை', 'Status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} onClick={() => nav(`/subscribers/${s.id}`)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="row-sub">
                      <div className="ava-sm">{initials(s.name_en)}</div>
                      <div>
                        <div className={lang === 'ta' ? 'tamil name-ta' : 'name-en'}>
                          {lang === 'ta' ? s.name_ta : s.name_en}
                        </div>
                        <div className="name-meta mono">{s.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {/* Chit count — dim when zero (member exists but isn't
                        in any chit yet), normal weight otherwise. */}
                    <span
                      className="mono"
                      style={{
                        fontSize: 13,
                        fontWeight: (s.chit_count ?? 0) > 0 ? 600 : 400,
                        color: (s.chit_count ?? 0) > 0 ? 'var(--ink)' : 'var(--ink-3)',
                      }}
                    >
                      {s.chit_count ?? 0}
                    </span>
                  </td>
                  <td className="mono" style={{ textAlign: 'right', color: (s.total_paid_paise ?? 0) > 0 ? 'var(--good)' : 'var(--ink-3)' }}>
                    {format_inr(s.total_paid_paise ?? 0)}
                  </td>
                  <td className="mono" style={{ textAlign: 'right', color: (s.outstanding_paise ?? 0) > 0 ? 'var(--warn)' : 'var(--ink-3)', fontWeight: (s.outstanding_paise ?? 0) > 0 ? 600 : 400 }}>
                    {format_inr(s.outstanding_paise ?? 0)}
                  </td>
                  <td>
                    <span className={'risk ' + s.risk_classification}>{s.risk_classification}</span>
                  </td>
                  <td>
                    {s.status === 'active' && (
                      <span className="status paid"><span className="dot" />{t('செயலில்', 'Active')}</span>
                    )}
                    {s.status === 'watch' && (
                      <span className="status pending"><span className="dot" />{t('கவனிப்பு', 'Watch')}</span>
                    )}
                    {s.status === 'defaulted' && (
                      <span className="status overdue"><span className="dot" />{t('பற்றாக்குறை', 'Default')}</span>
                    )}
                    {s.status !== 'active' && s.status !== 'watch' && s.status !== 'defaulted' && (
                      <span className="status pending"><span className="dot" />{s.status}</span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <NotifyMenu subscriber_id={s.id} intent="custom" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination footer — show only when there's more to load OR the
            loaded set has grown past one page (gives a "you've seen X" cue).
            Click triggers fetchNextPage; the next batch lands on the existing
            list via React Query's page accumulation. */}
        {(has_more || loaded_count > PAGE_SIZE) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: '12px 14px',
              borderTop: '1px solid var(--line)',
              background: 'var(--bg-elev)',
              fontSize: 12,
              color: 'var(--ink-3)',
            }}
          >
            <span>
              {t('காண்பிக்கப்படுகிறது', 'Showing')}{' '}
              <span className="mono" style={{ color: 'var(--ink)', fontWeight: 600 }}>
                {loaded_count}
              </span>
              {has_more ? ` ${t('இன்னும் கூடுதலாக', '(more available)')}` : ` ${t('அனைத்தும்', '(all loaded)')}`}
            </span>
            {has_more && (
              <button
                className="btn sm"
                disabled={q.isFetchingNextPage}
                onClick={() => q.fetchNextPage()}
              >
                {q.isFetchingNextPage
                  ? t('ஏற்றுகிறது…', 'Loading…')
                  : `${t('மேலும் காண்பி', 'Show more')} (+${PAGE_SIZE})`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
